import { useCallback, useState } from "react";
import {
  ScopingSchema,
  ScopingSection,
  ScopingQuestionOption,
  createDefaultSchema,
  normalizeOptions,
  validateScopingSchema
} from "./scoping-schema";
import { isChoiceType } from "./scoping-question-ui";

const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove non-alphanumeric (except space/dash)
    .replace(/[\s-]+/g, "_")  // Convert spaces/dashes to underscores
    .replace(/^_+|_+$/g, ""); // Trim leading/trailing underscores
};

export function useScopingSchemaEditor(initial: ScopingSchema | null = null) {
  const [schema, setSchema] = useState<ScopingSchema | null>(initial);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [expandedQids, setExpandedQids] = useState<Record<string, boolean>>({});
  const [previewSectionIdx, setPreviewSectionIdx] = useState(0);

  const activeSection = schema?.sections?.[activeSectionIdx] ?? null;
  const previewSection = schema?.sections?.[previewSectionIdx] ?? null;

  const resetEditor = useCallback((next: ScopingSchema | null, sectionIdx = 0) => {
    setSchema(next);
    setActiveSectionIdx(sectionIdx);
    setPreviewSectionIdx(sectionIdx);
    setExpandedQids({});
  }, []);

  const toggleQuestionExpand = useCallback((qid: string) => {
    setExpandedQids((prev) => ({ ...prev, [qid]: !prev[qid] }));
  }, []);

  const collapseAllQuestions = useCallback(() => setExpandedQids({}), []);

  const addSection = useCallback(() => {
    setSchema((prev) => {
      if (!prev) return prev;
      const n = prev.sections.length + 1;
      const newSection: ScopingSection = {
        id: `section_${Date.now()}`,
        title: `Section ${n}`,
        description: "",
        questions: []
      };
      const sections = [...prev.sections, newSection];
      setActiveSectionIdx(sections.length - 1);
      setPreviewSectionIdx(sections.length - 1);
      return { ...prev, sections };
    });
  }, []);

  const removeSection = useCallback((sectionIdx: number) => {
    setSchema((prev) => {
      if (!prev) return prev;
      const sections = prev.sections.filter((_, i) => i !== sectionIdx);
      const nextIdx = Math.max(0, sectionIdx - 1);
      setActiveSectionIdx(nextIdx);
      setPreviewSectionIdx(Math.min(nextIdx, sections.length - 1));
      return { ...prev, sections };
    });
  }, []);

  const updateSectionDetails = useCallback(
    (sectionIdx: number, field: "title" | "description", value: string) => {
      setSchema((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map((sec, i) =>
            i === sectionIdx ? { ...sec, [field]: value } : sec
          )
        };
      });
    },
    []
  );

  const addQuestion = useCallback((sectionIdx: number) => {
    setSchema((prev) => {
      if (!prev) return prev;
      const currentSec = prev.sections[sectionIdx];
      if (!currentSec) return prev;

      const n = currentSec.questions.length + 1;
      const sectionTitleSlug = slugify(currentSec.title || `section_${sectionIdx + 1}`);
      const initialLabel = `Question ${n}`;
      const initialLabelSlug = slugify(initialLabel);

      const newQid = `${sectionTitleSlug}_q${n}_${initialLabelSlug}`
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");

      setExpandedQids((e) => ({ ...e, [newQid]: true }));

      return {
        ...prev,
        sections: prev.sections.map((sec, i) => {
          if (i !== sectionIdx) return sec;
          return {
            ...sec,
            questions: [
              ...sec.questions,
              {
                qid: newQid,
                label: initialLabel,
                type: "short_text",
                required: false
              }
            ]
          };
        })
      };
    });
  }, []);

  const removeQuestion = useCallback((sectionIdx: number, qid: string) => {
    setSchema((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map((sec, i) =>
          i === sectionIdx
            ? { ...sec, questions: sec.questions.filter((q) => q.qid !== qid) }
            : sec
        )
      };
    });
    setExpandedQids((prev) => {
      const copy = { ...prev };
      delete copy[qid];
      return copy;
    });
  }, []);

  const duplicateQuestion = useCallback((sectionIdx: number, qid: string) => {
    setSchema((prev) => {
      if (!prev) return prev;
      const sec = prev.sections[sectionIdx];
      const source = sec?.questions.find((q) => q.qid === qid);
      if (!source) return prev;
      const newQid = `${source.qid}_copy_${Date.now()}`;
      setExpandedQids((e) => ({ ...e, [newQid]: true }));
      return {
        ...prev,
        sections: prev.sections.map((s, i) =>
          i === sectionIdx
            ? {
                ...s,
                questions: [
                  ...s.questions,
                  {
                    ...source,
                    qid: newQid,
                    label: `${source.label} (copy)`,
                    options: source.options
                      ? JSON.parse(JSON.stringify(source.options))
                      : undefined
                  }
                ]
              }
            : s
        )
      };
    });
  }, []);

  const updateQuestionLabel = useCallback(
    (sectionIdx: number, qid: string, label: string) => {
      setSchema((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map((sec, i) =>
            i === sectionIdx
              ? {
                  ...sec,
                  questions: sec.questions.map((q) =>
                    q.qid === qid ? { ...q, label } : q
                  )
                }
              : sec
          )
        };
      });
    },
    []
  );

  const autoGenerateKeyId = useCallback(
    (sectionIdx: number, qid: string) => {
      setSchema((prev) => {
        if (!prev) return prev;

        let targetQid = qid;
        const currentSec = prev.sections[sectionIdx];
        const currentQ = currentSec?.questions.find((q) => q.qid === qid);

        if (currentSec && currentQ) {
          const sectionTitleSlug = slugify(currentSec.title || `section_${sectionIdx + 1}`);
          const questionIndex = currentSec.questions.findIndex((q) => q.qid === qid);
          const questionNumber = questionIndex !== -1 ? questionIndex + 1 : currentSec.questions.length + 1;
          const labelSlug = slugify(currentQ.label);

          const generatedSlug = `${sectionTitleSlug}_q${questionNumber}_${labelSlug}`
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, "");

          const oldLabelSlug = slugify(currentQ.label);
          const oldSectionSlug = slugify(currentSec.title || `section_${sectionIdx + 1}`);
          const expectedOldSlug = `${oldSectionSlug}_q${questionNumber}_${oldLabelSlug}`
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, "");

          // We generate/auto-update if it matches a default timestamp/temporary pattern, duplicate string, or old slug pattern
          const isDefaultKey = /^q_\d+$/.test(qid) || qid.includes("_copy_") || qid.includes("_question_") || qid.startsWith("section_");
          const isMatchingOldSlug = qid === expectedOldSlug || qid === oldLabelSlug || qid === "";

          if ((isDefaultKey || isMatchingOldSlug) && generatedSlug) {
            // Check for uniqueness within the entire schema to avoid duplicates
            let uniqueSlug = generatedSlug;
            let counter = 1;
            const existingKeys = new Set<string>();
            prev.sections.forEach((s) =>
              s.questions.forEach((q) => {
                if (q.qid !== qid) existingKeys.add(q.qid);
              })
            );

            while (existingKeys.has(uniqueSlug)) {
              uniqueSlug = `${generatedSlug}_${counter}`;
              counter++;
            }
            targetQid = uniqueSlug;
          }
        }

        if (targetQid === qid) return prev;

        const updated = {
          ...prev,
          sections: prev.sections.map((sec, i) =>
            i === sectionIdx
              ? {
                  ...sec,
                  questions: sec.questions.map((q) =>
                    q.qid === qid ? { ...q, qid: targetQid } : q
                  )
                }
              : sec
          )
        };

        // Sync expanded state
        setExpandedQids((prevExpanded) => {
          if (!prevExpanded[qid]) return prevExpanded;
          const copy = { ...prevExpanded };
          delete copy[qid];
          copy[targetQid] = true;
          return copy;
        });

        return updated;
      });
    },
    [setExpandedQids]
  );

  const updateQuestionKeyId = useCallback(
    (sectionIdx: number, qid: string, newKeyId: string) => {
      const cleaned = newKeyId.trim().replace(/\s+/g, "_").toLowerCase();
      setSchema((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map((sec, i) =>
            i === sectionIdx
              ? {
                  ...sec,
                  questions: sec.questions.map((q) =>
                    q.qid === qid ? { ...q, qid: cleaned } : q
                  )
                }
              : sec
          )
        };
      });
      if (cleaned !== qid) {
        setExpandedQids((prev) => {
          if (!prev[qid]) return prev;
          const copy = { ...prev };
          delete copy[qid];
          copy[cleaned] = true;
          return copy;
        });
      }
    },
    []
  );

  const toggleQuestionRequired = useCallback((sectionIdx: number, qid: string) => {
    setSchema((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map((sec, i) =>
          i === sectionIdx
            ? {
                ...sec,
                questions: sec.questions.map((q) =>
                  q.qid === qid ? { ...q, required: !q.required } : q
                )
              }
            : sec
        )
      };
    });
  }, []);

  const updateQuestionType = useCallback(
    (sectionIdx: number, qid: string, newType: string) => {
      setSchema((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map((sec, i) =>
            i === sectionIdx
              ? {
                  ...sec,
                  questions: sec.questions.map((q) => {
                    if (q.qid !== qid) return q;
                    const choice = isChoiceType(newType);
                    const defaultOpts: ScopingQuestionOption[] = [
                      { value: "option_1", label: "Option 1" }
                    ];
                    return {
                      ...q,
                      type: newType,
                      options: choice
                        ? normalizeOptions(q.options).length
                          ? normalizeOptions(q.options)
                          : defaultOpts
                        : undefined
                    };
                  })
                }
              : sec
          )
        };
      });
    },
    []
  );

  const addQuestionOption = useCallback((sectionIdx: number, qid: string) => {
    setSchema((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map((sec, i) =>
          i === sectionIdx
            ? {
                ...sec,
                questions: sec.questions.map((q) => {
                  if (q.qid !== qid) return q;
                  const opts = normalizeOptions(q.options);
                  const n = opts.length + 1;
                  return {
                    ...q,
                    options: [
                      ...opts,
                      { value: `option_${n}`, label: `Option ${n}` }
                    ]
                  };
                })
              }
            : sec
        )
      };
    });
  }, []);

  const updateQuestionOption = useCallback(
    (
      sectionIdx: number,
      qid: string,
      optionIdx: number,
      field: "label" | "value",
      value: string
    ) => {
      setSchema((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map((sec, i) =>
            i === sectionIdx
              ? {
                  ...sec,
                  questions: sec.questions.map((q) => {
                    if (q.qid !== qid) return q;
                    const newOpts = normalizeOptions(q.options).map((opt, oi) => {
                      if (oi !== optionIdx) return opt;
                      if (field === "label") {
                        return { ...opt, label: value, value: slugify(value) };
                      }
                      return { ...opt, [field]: value };
                    });
                    return { ...q, options: newOpts };
                  })
                }
              : sec
          )
        };
      });
    },
    []
  );

  const removeQuestionOption = useCallback(
    (sectionIdx: number, qid: string, optionIdx: number) => {
      setSchema((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map((sec, i) =>
            i === sectionIdx
              ? {
                  ...sec,
                  questions: sec.questions.map((q) =>
                    q.qid === qid
                      ? {
                          ...q,
                          options: normalizeOptions(q.options).filter(
                            (_opt, oi) => oi !== optionIdx
                          )
                        }
                      : q
                  )
                }
              : sec
          )
        };
      });
    },
    []
  );

  const validate = useCallback(() => validateScopingSchema(schema), [schema]);

  return {
    schema,
    setSchema,
    activeSectionIdx,
    setActiveSectionIdx,
    previewSectionIdx,
    setPreviewSectionIdx,
    activeSection,
    previewSection,
    expandedQids,
    resetEditor,
    toggleQuestionExpand,
    collapseAllQuestions,
    addSection,
    removeSection,
    updateSectionDetails,
    addQuestion,
    removeQuestion,
    duplicateQuestion,
    updateQuestionLabel,
    autoGenerateKeyId,
    updateQuestionKeyId,
    toggleQuestionRequired,
    updateQuestionType,
    addQuestionOption,
    updateQuestionOption,
    removeQuestionOption,
    validate,
    createDefaultSchema
  };
}

export type ScopingSchemaEditor = ReturnType<typeof useScopingSchemaEditor>;
